import { COLS, ROWS } from './types'

/** 棋盘度量：格距与内边距。 */
export interface BoardMetrics {
  readonly rows: number
  readonly cols: number
  readonly cell: number
  readonly pad: number
}

const MIN_CELL = 18
const MAX_CELL = 54
// 旧版 updateBoardSize() 会从可用区域里各减去 4px，避免棋盘贴边
const INSET = 4

/** 默认度量：10×9 棋盘、54px 格距、30px 内边距。 */
export const DEFAULT_METRICS: BoardMetrics = { rows: ROWS, cols: COLS, cell: MAX_CELL, pad: 30 }

/** 逻辑坐标 → 屏幕坐标；flip 时 180° 旋转（互为逆变换）。 */
export function flipRC(r: number, c: number, flip: boolean): { r: number; c: number } {
  return flip ? { r: ROWS - 1 - r, c: COLS - 1 - c } : { r, c }
}

/** 棋盘像素尺寸。 */
export function boardSize(m: BoardMetrics): { width: number; height: number } {
  return {
    width: m.pad * 2 + (m.cols - 1) * m.cell,
    height: m.pad * 2 + (m.rows - 1) * m.cell,
  }
}

/** 逻辑格中心点（屏幕坐标）。 */
export function cellCenter(r: number, c: number, m: BoardMetrics, flip: boolean): { x: number; y: number } {
  const { r: sr, c: sc } = flipRC(r, c, flip)
  return { x: m.pad + sc * m.cell, y: m.pad + sr * m.cell }
}

/**
 * 给定棋盘容器内的相对坐标，反推逻辑格。
 *
 * 方形就近判定（与旧版 `getCellFromEvent` 等价）：先按格距 `Math.round` 取最近格，
 * 再校验行列落在 `[0, rows-1] / [0, cols-1]` 内；越界返回 null。
 * 不要在格子角落留死区——旧版的距离判断恒为假，棋盘内任意位置（含格子四角）点击
 * 都会落到最近格。
 */
export function cellFromPoint(
  x: number,
  y: number,
  m: BoardMetrics,
  flip: boolean,
): { r: number; c: number } | null {
  const sc = Math.round((x - m.pad) / m.cell)
  const sr = Math.round((y - m.pad) / m.cell)
  if (sc < 0 || sc > m.cols - 1 || sr < 0 || sr > m.rows - 1) return null
  const dx = Math.abs(x - (m.pad + sc * m.cell))
  const dy = Math.abs(y - (m.pad + sr * m.cell))
  // 方形判定：区间由取整结果决定，故这里恒成立——格子角落同样命中，没有圆形死区
  if (dx > m.cell / 2 || dy > m.cell / 2) return null
  return flipRC(sr, sc, flip)
}

/** 按可用区域同时约束宽高取小值，得到格距（移动端必须整屏放下）。 */
export function fitCell(availW: number, availH: number, m: BoardMetrics): number {
  const cellW = Math.floor((availW - INSET) / m.cols)
  const cellH = Math.floor((availH - INSET) / m.rows)
  return Math.max(MIN_CELL, Math.min(MAX_CELL, Math.min(cellW, cellH)))
}